/**
 * SimulatedPositionProvider (#441 Phase 2) — flies a synthesized leg
 * along the great circle between two airports.
 *
 * It is a `PositionProvider` and nothing more: it fabricates the same
 * `GeolocationPosition` shape the browser emits, so `_onPosition`,
 * staleness, the forced-refresh timer, the store, the map and the NOTAM
 * list run unchanged. The simulator exercises the real tracking code —
 * that is the whole point of the seam.
 *
 * Two rules it must not break, both learned in the review of PR #445:
 *
 * - **It never calls `onError`.** The error channel carries the browser's
 *   `GeolocationPositionError` shape, and a fabricated `PERMISSION_DENIED`
 *   would reach the permission singleton and lock the pilot out of REAL
 *   GPS for the session. A bad route is reported by `start()` returning
 *   `false`, before anything is armed.
 * - **It emits nothing synchronously from `start()`.** The loop marks
 *   itself running only once `start()` returns, so a synchronous first fix
 *   would be dropped. The first fix lands on a zero-delay timer instead,
 *   which is immediate to a user but asynchronous to the loop.
 *
 * Progress is accumulated as DISTANCE FLOWN, not elapsed time, so a mid-
 * flight change of speed or time acceleration applies from that moment
 * forward instead of retroactively rescaling the leg already flown.
 */
import { config } from '@/config/config';
import {
  distanceM,
  initialBearingDeg,
  interpolate,
  isDegenerateLeg,
  isValidLatLon,
  type LatLon
} from '@/utils/greatCircle';
import { FEET_PER_METER, METERS_PER_SECOND_PER_KNOT } from '@/utils/units';
import type { PositionProvider, PositionProviderKind } from './PositionProvider';

/** Below this, the aircraft is on top of the destination and the bearing is undefined. */
const BEARING_MIN_REMAINING_M = 1;

export interface SimulatedLegOptions {
  /** Departure, in degrees. Callers convert from GeoJSON `[lng, lat]`. */
  from: LatLon;
  /** Destination, in degrees. */
  to: LatLon;
  groundSpeedKt?: number;
  altitudeFt?: number;
  timeAcceleration?: number;
}

export class SimulatedPositionProvider implements PositionProvider {
  readonly kind: PositionProviderKind = 'simulated';
  readonly fixesAge = true;

  private readonly _from: LatLon;
  private readonly _to: LatLon;
  private _groundSpeedKt: number;
  private _altitudeFt: number;
  private _timeAcceleration: number;

  private _legLengthM = 0;
  private _flownM = 0;
  private _lastTickMs = 0;
  private _lastHeadingDeg = 0;
  private _paused = false;

  private _timer: ReturnType<typeof setInterval> | null = null;
  private _firstFixTimer: ReturnType<typeof setTimeout> | null = null;
  private _onPosition: ((pos: GeolocationPosition) => void) | null = null;

  constructor(options: SimulatedLegOptions) {
    this._from = { lat: options.from.lat, lon: options.from.lon };
    this._to = { lat: options.to.lat, lon: options.to.lon };
    this._groundSpeedKt = options.groundSpeedKt ?? config.simulator.defaultGroundSpeedKt;
    this._altitudeFt = options.altitudeFt ?? config.simulator.defaultAltitudeFt;
    this._timeAcceleration = this._clampAcceleration(
      options.timeAcceleration ?? config.simulator.defaultTimeAcceleration
    );
  }

  // --- PositionProvider ------------------------------------------------

  start(onPosition: (pos: GeolocationPosition) => void): boolean {
    // Already armed — a second arm would run two integrators against one
    // callback, doubling the fixes and the API calls behind them.
    if (this._timer !== null) return true;

    // Input validation at the trust boundary: the endpoints come from a
    // briefing the user typed, so a missing or degenerate route reaches
    // here as data, not as a programming error.
    if (!isValidLatLon(this._from) || !isValidLatLon(this._to)) {
      // eslint-disable-next-line no-console
      console.error('[SimulatedPositionProvider] invalid leg endpoints');
      return false;
    }
    if (!Number.isFinite(this._groundSpeedKt) || this._groundSpeedKt <= 0) {
      // eslint-disable-next-line no-console
      console.error('[SimulatedPositionProvider] ground speed must be positive');
      return false;
    }
    if (isDegenerateLeg(this._from, this._to)) {
      // The same point has no direction; antipodal points have infinitely
      // many shortest paths. Neither is a leg.
      // eslint-disable-next-line no-console
      console.error('[SimulatedPositionProvider] the two endpoints do not define a leg');
      return false;
    }
    this._legLengthM = distanceM(this._from, this._to);

    this._flownM = 0;
    this._paused = false;
    this._lastHeadingDeg = initialBearingDeg(this._from, this._to);
    this._lastTickMs = Date.now();
    this._onPosition = onPosition;

    // Asynchronous, but immediate to the user: the aircraft appears at the
    // departure airport rather than a tick later.
    this._firstFixTimer = setTimeout(() => {
      this._firstFixTimer = null;
      this._tick();
    }, 0);
    this._timer = setInterval(() => this._tick(), config.simulator.tickMs);
    return true;
  }

  stop(): void {
    if (this._firstFixTimer !== null) {
      clearTimeout(this._firstFixTimer);
      this._firstFixTimer = null;
    }
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
    this._onPosition = null;
  }

  // --- Flight controls --------------------------------------------------

  /** Freeze the aircraft. Fixes keep flowing, so the position never goes stale. */
  pause(): void {
    this._bankDistance();
    this._paused = true;
  }

  resume(): void {
    this._bankDistance();
    this._paused = false;
  }

  setGroundSpeedKt(kt: number): void {
    if (!Number.isFinite(kt) || kt <= 0) return;
    this._bankDistance();
    this._groundSpeedKt = kt;
    // Flying faster lowers the acceleration ceiling, because what the
    // budget limits is metres per tick, not the multiplier itself. Without
    // this, dialling the speed up would silently breach the API budget at
    // an acceleration that was legal a moment ago.
    this._timeAcceleration = this._clampAcceleration(this._timeAcceleration);
  }

  setAltitudeFt(ft: number): void {
    if (!Number.isFinite(ft)) return;
    this._altitudeFt = ft;
  }

  setTimeAcceleration(multiplier: number): void {
    this._bankDistance();
    this._timeAcceleration = this._clampAcceleration(multiplier);
  }

  // --- Read-only state for the UI ---------------------------------------

  get isPaused(): boolean {
    return this._paused;
  }

  /** 0 at the departure, 1 at the destination. */
  get progress(): number {
    if (this._legLengthM <= 0) return 0;
    return Math.min(1, this._flownM / this._legLengthM);
  }

  get isFinished(): boolean {
    return this._legLengthM > 0 && this._flownM >= this._legLengthM;
  }

  get groundSpeedKt(): number {
    return this._groundSpeedKt;
  }

  get altitudeFt(): number {
    return this._altitudeFt;
  }

  get timeAcceleration(): number {
    return this._timeAcceleration;
  }

  /**
   * The highest multiplier this leg may be flown at RIGHT NOW. Lower than
   * `config.simulator.maxTimeAcceleration` whenever the ground speed is
   * high enough that a tick would otherwise advance past the move trigger
   * and make the loop call the API every tick.
   */
  get maxAllowedAcceleration(): number {
    const tickSeconds = config.simulator.tickMs / 1000;
    const speedMs = this._groundSpeedKt * METERS_PER_SECOND_PER_KNOT;
    if (!(speedMs > 0) || !(tickSeconds > 0)) return config.simulator.maxTimeAcceleration;
    const budgetM = config.geolocation.minDistanceM * config.simulator.apiBudgetFraction;
    return Math.min(config.simulator.maxTimeAcceleration, budgetM / (speedMs * tickSeconds));
  }

  // --- Internals --------------------------------------------------------

  /**
   * Hold the multiplier at a CONFIGURED step, never at the raw ceiling.
   * Clamping to the float put `7.69438444924406x` on the control, and that
   * value is not a member of the step list, so the next tap fell off the
   * cycle entirely.
   *
   * 1x is always allowed: at real time a simulated leg covers exactly what
   * the real one does, so it cannot exceed its API budget by definition.
   */
  private _clampAcceleration(multiplier: number): number {
    const requested =
      Number.isFinite(multiplier) && multiplier > 0
        ? multiplier
        : config.simulator.defaultTimeAcceleration;
    const ceiling = this.maxAllowedAcceleration;
    const fits = config.simulator.accelerationSteps.filter(
      (step) => step <= ceiling && step <= requested
    );
    return fits.length > 0 ? Math.max(...fits) : 1;
  }

  /**
   * Credit the distance flown since the last accounting moment and move
   * the clock forward. Called before any change to speed or acceleration
   * so the new value applies only from here on.
   */
  private _bankDistance(): void {
    const now = Date.now();
    // Credit at most `maxCatchUpTicks` worth of flight, however long the
    // gap really was. Two reasons, both measured against the same number.
    // A tick that lands late (the map re-renders on this thread) would
    // otherwise advance further than the API budget allows and make the
    // loop call the server on that tick. And a BACKGROUNDED tab, which
    // browsers throttle to roughly one timer per minute, would bank a
    // whole minute in one step — at 8x that is a 27 km jump, skipping
    // every proximity event the feature exists to preview.
    const maxStepSeconds =
      (config.simulator.tickMs / 1000) * config.simulator.maxCatchUpTicks;
    const wallSeconds = Math.min(
      maxStepSeconds,
      Math.max(0, (now - this._lastTickMs) / 1000)
    );
    this._lastTickMs = now;
    if (this._paused || this.isFinished) return;
    const speedMs = this._groundSpeedKt * METERS_PER_SECOND_PER_KNOT;
    this._flownM = Math.min(
      this._legLengthM,
      this._flownM + speedMs * wallSeconds * this._timeAcceleration
    );
  }

  private _tick(): void {
    this._bankDistance();
    const emit = this._onPosition;
    if (emit === null) return;

    const here = interpolate(this._from, this._to, this.progress);
    // Along a great circle the course changes continuously, so the
    // heading is recomputed from where the aircraft IS. At the
    // destination it is undefined — hold the last one rather than snap.
    const remainingM = distanceM(here, this._to);
    if (remainingM > BEARING_MIN_REMAINING_M) {
      this._lastHeadingDeg = initialBearingDeg(here, this._to);
    }

    emit({
      coords: {
        latitude: here.lat,
        longitude: here.lon,
        altitude: this._altitudeFt / FEET_PER_METER,
        accuracy: config.simulator.accuracyM,
        altitudeAccuracy: null,
        heading: this._lastHeadingDeg,
        // A parked aircraft reports no ground speed.
        speed:
          this._paused || this.isFinished
            ? 0
            : this._groundSpeedKt * METERS_PER_SECOND_PER_KNOT
      } as GeolocationCoordinates,
      timestamp: Date.now()
    } as GeolocationPosition);
  }
}


